import { useEffect, useMemo, useRef } from 'react';

import { useGetBusinessPlan } from 'services/businessPlan/hooks';
import { useGetBusinessPlanChapters } from 'services/businessToolForm/hooks/useGetBusinessPlanChapters';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import {
  getBusinessPlanChapters,
  getSetActiveSection,
  getSetActiveSubSection,
  getSetBusinessPlan,
  getSetBusinessPlanChapters,
} from 'store/useBusinessToolStore/selectors';
import { useUserStore } from 'store/useUserStore';
import { getAuthUser } from 'store/useUserStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useExamplePreview } from 'hooks/useExamplePreview';

import { useGetInitialValues } from 'components/BusinessToolForm/utils/form';

import { getFormValueFromChapters } from 'helpers/businessTool';
import { getActiveFormSection, getActiveFormSubSection } from 'helpers/setActiveFormSection';

interface IProps {
  businessPlanId: string;
  disableStoreSave?: boolean;
}

export const useBusinessToolPageLogic = ({ businessPlanId, disableStoreSave }: IProps) => {
  const authUser = useUserStore(getAuthUser);
  const localizedInitialValues = useGetInitialValues();

  const localStoredChapters = useBusinessToolStore(getBusinessPlanChapters);
  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);
  const setActiveSection = useBusinessToolStore(getSetActiveSection);
  const setActiveSubSection = useBusinessToolStore(getSetActiveSubSection);

  const setBusinessPlanChapters = useBusinessToolStore(getSetBusinessPlanChapters);
  const { isExamplePage } = useExamplePreview();

  const { isAuthenticated } = useAuthenticated();
  const {
    data: businessPlan,
    isLoading,
    error,
  } = useGetBusinessPlan({ id: businessPlanId, enabled: !!authUser && isAuthenticated });

  const {
    data: businessPlanSections,
    isLoading: isSectionsLoading,
    error: errorSections,
  } = useGetBusinessPlanChapters({ id: businessPlanId, enabled: isExamplePage ? true : !!authUser });
  const lastHydratedSectionsRef = useRef<typeof businessPlanSections>();

  const needSetBusinessPlan = ((businessPlan && isExamplePage) || (businessPlan && !!authUser)) && !disableStoreSave;
  const needSetBusinessPlanChapters =
    ((businessPlanSections && !!authUser) || (businessPlanSections && isExamplePage)) && !disableStoreSave;
  const needSetActiveSections = !!businessPlan && !disableStoreSave;

  useEffect(() => {
    if (needSetBusinessPlan) {
      setBusinessPlan(businessPlan);
    }
  }, [businessPlan, setBusinessPlan]);

  useEffect(() => {
    if (needSetActiveSections) {
      setActiveSection(getActiveFormSection(businessPlan.type));
      setActiveSubSection(getActiveFormSubSection(businessPlan.type));
    }
  }, []);

  useEffect(() => {
    if (needSetBusinessPlanChapters && lastHydratedSectionsRef.current !== businessPlanSections) {
      setBusinessPlanChapters(getFormValueFromChapters(businessPlanSections, localizedInitialValues));
      lastHydratedSectionsRef.current = businessPlanSections;
    }
  }, [businessPlanSections, localizedInitialValues, setBusinessPlanChapters]);

  const initialValues = useMemo(() => {
    if (authUser) {
      return businessPlanSections
        ? getFormValueFromChapters(businessPlanSections, localizedInitialValues)
        : localizedInitialValues;
    }

    return localStoredChapters;
    // removed localStoredChapters from dependencies to prevent re-rendering. I need it only once
  }, [authUser, businessPlanSections, localizedInitialValues]);

  return {
    isLoading: isLoading || isSectionsLoading,
    error: error || errorSections,
    initialValues,
  };
};
